import React from 'react';
import { X, FileText, Upload, Plus, Loader2, Trash2 } from 'lucide-react';
import { Avatar } from '../../../../../dashboard/components/DashboardComponents';
import useDocumentInfo from '../../hooks/useDocumentInfo';
import './DocumentInfoModal.css';

/**
 * Modal component for managing and assigning analyzed documents.
 * All state and API logic is delegated to useDocumentInfo hook.
 */
const DocumentInfoModal = ({
    isOpen, onClose, documents, setDocuments,
    documentAssignments, setDocumentAssignments,
    teamMembers, rfpId, isManager, fetchAssignedUsersData,
    isTeamLeadViewOnly = false
}) => {
    const {
        showDropdownForDoc, setShowDropdownForDoc,
        isLoading, alertData, setAlertData,
        deleteConfirm, setDeleteConfirm,
        fileDeleteConfirm, setFileDeleteConfirm,
        uploadConfirm, setUploadConfirm,
        fileInputRef, selectRef, membersList,
        handleAddMember, handleRemoveMember,
        confirmRemoveAccess, initiateDeleteFile,
        confirmDeleteFile, handleConfirm,
        handleUploadClick, handleFileChange,
        confirmUpload, handleShowDropdown
    } = useDocumentInfo({
        isOpen, rfpId, isManager, setDocuments, setDocumentAssignments,
        documents, documentAssignments, teamMembers, onClose, fetchAssignedUsersData
    });

    if (!isOpen) return null;

    return (
        <div className="ai-modal-overlay">
            <div className="ai-modal-content assign-modal document-info-modal">
                <div className="ai-modal-header">
                    <div className="ai-modal-title">
                        <FileText size={20} color="#64748B" />
                        <h4>Manage Access</h4>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="document-modal-toolbar">
                    <p className="document-modal-desc">
                        {isManager
                            ? 'Upload, view, and assign specific reference documents to individual team members.'
                            : 'View the list of documents used for this RFP analysis.'}
                    </p>
                    {isManager && (
                        <>
                            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                            <button 
                                className="primary-action-btn upload-doc-btn" 
                                onClick={() => !isTeamLeadViewOnly && handleUploadClick()}
                                disabled={isTeamLeadViewOnly}
                                style={{
                                    opacity: isTeamLeadViewOnly ? 0.5 : 1,
                                    cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <Upload size={12} />
                                <span>Upload New Document</span>
                            </button>
                        </>
                    )}
                </div>

                <div className="ai-modal-body assign-modal-body">
                    {isLoading ? (
                        <div style={{ padding: '3rem', textAlign: 'center', color: '#FF5526' }}>
                            <Loader2 className="animate-spin" size={32} style={{ margin: '0 auto 1rem' }} />
                            <p>Loading documents...</p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                            <p>No documents uploaded yet.</p>
                        </div>
                    ) : documents.map((doc) => {
                        const documentId = doc.doc_id || doc.id;
                        const assignedMembers = documentAssignments[documentId] || [];
                        const isAdding = showDropdownForDoc === documentId;

                        // Dynamically resolve assigned users from documentAssignments[documentId]
                        const resolvedAssignedUsers = assignedMembers.map(userId => {
                            const foundInMembers = membersList.find(m => String(m.user_id || m._id || m.id) === String(userId));
                            if (foundInMembers) {
                                return {
                                    id: userId,
                                    first_name: foundInMembers.first_name || foundInMembers.name?.split(' ')[0] || foundInMembers.name || '',
                                    last_name: foundInMembers.last_name || foundInMembers.name?.split(' ').slice(1).join(' ') || '',
                                    email: foundInMembers.email || '',
                                    avatar_url: foundInMembers.avatar_url || ''
                                };
                            }
                            const foundInAllowed = doc.allowed_users?.find(u => String(u.id || u.user_id) === String(userId));
                            if (foundInAllowed) {
                                return foundInAllowed;
                            }
                            return {
                                id: userId,
                                first_name: 'User',
                                last_name: String(userId).slice(0, 4)
                            };
                        });

                        // Check if all members in membersList are already assigned
                        const unassignedMembers = membersList.filter(m => !assignedMembers.includes(String(m.user_id || m._id || m.id)));
                        const hasUnassignedMembers = unassignedMembers.length > 0;

                        return (
                            <div key={documentId} className="assign-row-redesign document-row">
                                <div
                                    className="assign-left-col doc-left-col"
                                    onClick={() => doc.url && window.open(doc.url, '_blank')}
                                    style={{ cursor: doc.url ? 'pointer' : 'default' }}
                                    title={doc.url ? 'Click to open file' : ''}
                                >
                                    <div className="assign-section-badge doc-icon-badge"><FileText size={16} /></div>
                                    <div className="doc-info-group">
                                        <h5 className="assign-section-title">{doc.filename || doc.name}</h5>
                                        <span className="doc-size-label">{doc.size || 'Unknown Size'}</span>
                                    </div>
                                </div>
                                <div className="assign-right-col">
                                    <div className="assigned-members-stack" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                        {isManager ? (
                                            /* Team Lead: Display "Assigned to" representing resolvedAssignedUsers */
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                                {resolvedAssignedUsers.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end' }}>
                                                        <span style={{ fontSize: '11px', color: '#64748B', alignSelf: 'center', marginRight: '4px', fontWeight: '500' }}>Assigned to:</span>
                                                        {resolvedAssignedUsers.map(u => (
                                                            <div 
                                                                key={u.id || u.user_id} 
                                                                className="member-pill-target" 
                                                                style={{ 
                                                                    cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer', 
                                                                    background: '#F1F5F9', 
                                                                    border: '1px solid #E2E8F0',
                                                                    opacity: isTeamLeadViewOnly ? 0.6 : 1
                                                                }} 
                                                                onClick={() => !isTeamLeadViewOnly && handleRemoveMember(documentId, u.id || u.user_id)}
                                                            >
                                                                <div className="member-pill-avatar-target-wrapper" style={{ flexShrink: 0, display: 'flex' }}>
                                                                    <Avatar name={`${u.first_name || ''} ${u.last_name || ''}`.trim()} size={20} />
                                                                </div>
                                                                <span className="member-pill-name-target" style={{ color: '#334155' }}>
                                                                    {(u.first_name || '').trim()} {(u.last_name || '').trim()}
                                                                </span>
                                                                <span style={{ marginLeft: '4px', display: 'flex', alignItems: 'center', color: '#94A3B8' }} title={isTeamLeadViewOnly ? "" : "Remove access"}>
                                                                    <X size={10} />
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic', marginRight: '8px' }}>Not assigned to anyone</span>
                                                )}
                                            </div>
                                        ) : (
                                            /* Team Member: Display "Assigned by" representing doc.uploaded_by */
                                            doc.uploaded_by && (
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '500' }}>Assigned by:</span>
                                                    <div className="member-pill-target" style={{ cursor: 'default' }}>
                                                        <div className="member-pill-avatar-target-wrapper" style={{ flexShrink: 0, display: 'flex' }}>
                                                                    <Avatar name={`${doc.uploaded_by.first_name || ''} ${doc.uploaded_by.last_name || ''}`.trim()} size={20} />
                                                                </div>
                                                        <span className="member-pill-name-target">
                                                            {(doc.uploaded_by.first_name || '').trim()} {(doc.uploaded_by.last_name || '').trim()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                        {isManager && (
                                            isAdding ? (
                                                <div className="inline-member-select">
                                                    <select ref={selectRef} className="inline-dropdown" onChange={(e) => handleAddMember(documentId, e.target.value)} autoFocus onBlur={() => setTimeout(() => setShowDropdownForDoc(null), 200)}>
                                                        <option value="">Select Member...</option>
                                                        {isManager && membersList.length > 1 && (
                                                            <option value="all" style={{ fontWeight: 'bold', color: '#FF5526' }}>All Contributors</option>
                                                        )}
                                                        {membersList.filter(m => !assignedMembers.includes(String(m.user_id || m._id || m.id))).map(m => (
                                                            <option key={m.user_id || m._id || m.id} value={m.user_id || m._id || m.id}>
                                                                {m.name || `${m.first_name} ${m.last_name}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    {hasUnassignedMembers && (
                                                        assignedMembers.length > 0 ? (
                                                            <button 
                                                                className="add-member-icon-btn" 
                                                                title={isTeamLeadViewOnly ? "" : "Add more contributors"} 
                                                                onClick={() => !isTeamLeadViewOnly && handleShowDropdown(documentId)}
                                                                disabled={isTeamLeadViewOnly}
                                                                style={{ opacity: isTeamLeadViewOnly ? 0.5 : 1, cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                <Plus size={14} />
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                className="assign-dotted-btn" 
                                                                onClick={() => !isTeamLeadViewOnly && handleShowDropdown(documentId)}
                                                                disabled={isTeamLeadViewOnly}
                                                                style={{ opacity: isTeamLeadViewOnly ? 0.5 : 1, cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                <span>+ Manage Access</span>
                                                            </button>
                                                        )
                                                    )}
                                                    {doc.doc_type !== 'rfp' && (
                                                        <button 
                                                            className="delete-file-btn" 
                                                            onClick={() => !isTeamLeadViewOnly && initiateDeleteFile(doc)} 
                                                            title={isTeamLeadViewOnly ? "" : "Delete document"}
                                                            disabled={isTeamLeadViewOnly}
                                                            style={{ opacity: isTeamLeadViewOnly ? 0.5 : 1, cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {isManager && (
                    <div className="ai-modal-footer-target" style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end' }}>
                        <button 
                            className="confirm-assignments-btn" 
                            onClick={() => !isTeamLeadViewOnly && handleConfirm()} 
                            disabled={isLoading || isTeamLeadViewOnly} 
                            style={{ 
                                padding: '0.6rem 2rem', 
                                fontSize: '0.85rem',
                                opacity: isTeamLeadViewOnly ? 0.5 : 1,
                                cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isLoading ? 'Updating...' : 'Confirm'}
                        </button>
                    </div>
                )}
            </div>

            {/* Alert Modal */}
            {alertData.show && (
                <div className="custom-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="stage-modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="stage-modal-header" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}><h3 style={{ fontSize: '1.1rem', fontWeight: '700' }}>Notice</h3></div>
                        <div className="stage-modal-body" style={{ padding: '1rem 1.5rem 2rem 1.5rem' }}><p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.5' }}>{alertData.message}</p></div>
                        <div className="stage-modal-actions" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'center' }}>
                            <button className="stage-modal-confirm" style={{ width: '100px', padding: '0.6rem', background: '#FF5526', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600' }}
                                onClick={() => { if (alertData.onOk) alertData.onOk(); setAlertData({ show: false, message: '', type: 'notice' }); }}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Access Confirmation */}
            {deleteConfirm.show && (
                <div className="custom-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="stage-modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="stage-modal-header" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}><h3 style={{ fontSize: '1.1rem', fontWeight: '700' }}>Confirm Deletion</h3></div>
                        <div className="stage-modal-body" style={{ padding: '1rem 1.5rem 2rem 1.5rem' }}><p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.5' }}><strong>{deleteConfirm.userName}</strong> is being removed access to <strong>{deleteConfirm.fileName}</strong>.</p></div>
                        <div className="stage-modal-actions" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                            <button className="stage-modal-cancel" style={{ flex: 1, padding: '0.6rem', background: '#F1F5F9', border: 'none', borderRadius: '8px', color: '#64748B', fontWeight: '600' }} onClick={() => setDeleteConfirm({ ...deleteConfirm, show: false })}>Cancel</button>
                            <button className="stage-modal-confirm" style={{ flex: 1, padding: '0.6rem', background: '#EF4444', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600' }} onClick={confirmRemoveAccess}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete File Confirmation */}
            {fileDeleteConfirm.show && (
                <div className="custom-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="stage-modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="stage-modal-header" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}><h3 style={{ fontSize: '1.1rem', fontWeight: '700' }}>Confirm Document Deletion</h3></div>
                        <div className="stage-modal-body" style={{ padding: '1rem 1.5rem 2rem 1.5rem' }}><p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.5' }}>Are you sure you want to delete <strong>{fileDeleteConfirm.fileName}</strong>? This action cannot be undone.</p></div>
                        <div className="stage-modal-actions" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                            <button className="stage-modal-cancel" style={{ flex: 1, padding: '0.6rem', background: '#F1F5F9', border: 'none', borderRadius: '8px', color: '#64748B', fontWeight: '600' }} onClick={() => setFileDeleteConfirm({ ...fileDeleteConfirm, show: false })}>Cancel</button>
                            <button className="stage-modal-confirm" style={{ flex: 1, padding: '0.6rem', background: '#EF4444', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600' }} onClick={confirmDeleteFile}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Confirmation */}
            {uploadConfirm.show && (
                <div className="custom-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="stage-modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="stage-modal-header" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}><h3 style={{ fontSize: '1.1rem', fontWeight: '700' }}>Upload Document</h3></div>
                        <div className="stage-modal-body" style={{ padding: '1rem 1.5rem 2rem 1.5rem' }}><p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.5', marginBottom: '1rem' }}>File: <strong>{uploadConfirm.file?.name}</strong></p></div>
                        <div className="stage-modal-actions" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                            <button className="stage-modal-cancel" style={{ flex: 1, padding: '0.6rem', background: '#F1F5F9', border: 'none', borderRadius: '8px', color: '#64748B', fontWeight: '600', cursor: 'pointer' }} onClick={() => setUploadConfirm({ show: false, file: null, reanalyze: false })}>Cancel</button>
                            <button className="stage-modal-confirm" style={{ flex: 1, padding: '0.6rem', background: '#FF5526', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer' }} onClick={confirmUpload}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentInfoModal;
